import React from 'react';
import styled from 'styled-components';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Icon from 'components/Icon';
import PageContainer from 'components/PageContainer';
import { HeaderContainer, HeaderTitle } from 'components/PageHeader';
import PageHeaderIcon from 'components/PageHeaderIcon';

export const getFacebookLink = () => {
  if (process.env.REACT_APP_PLATFORM === 'pitpay') {
    return 'https://www.facebook.com/PitPayApp';
  }

  if (process.env.REACT_APP_PLATFORM === 'kartpass') {
    return 'https://www.facebook.com/KartPassApp/';
  }

  if (process.env.REACT_APP_PLATFORM === 'tickethoss') {
    return 'https://www.facebook.com/TicketHoss/';
  }

  return 'https://www.facebook.com/PitPayApp';
};

export const getTwitterLink = () => {
  if (process.env.REACT_APP_PLATFORM === 'pitpay') {
    return 'https://twitter.com/pitpayapp';
  }

  if (process.env.REACT_APP_PLATFORM === 'kartpass') {
    return 'https://twitter.com/kartpassapp';
  }

  if (process.env.REACT_APP_PLATFORM === 'tickethoss') {
    return 'https://twitter.com/tickethoss';
  }

  return 'https://twitter.com/pitpayapp';
};

export const getInstagramLink = () => {
  if (process.env.REACT_APP_PLATFORM === 'pitpay') {
    return 'https://www.instagram.com/pitpayapp';
  }

  if (process.env.REACT_APP_PLATFORM === 'kartpass') {
    return 'https://www.instagram.com/kartpassapp/';
  }

  if (process.env.REACT_APP_PLATFORM === 'tickethoss') {
    return 'https://www.instagram.com/tickethoss/';
  }

  return 'https://www.instagram.com/pitpayapp';
};

const getFormLink = (form) => {
  const platform = process.env.REACT_APP_PLATFORM;
  const forms = {
    tickethoss: {
      new_event: 'https://form.jotform.com/241994930773166',
      event_change: 'https://form.jotform.com/232355700099153',
      help_site: 'https://help.tickethoss.com',
      new_season: 'https://formcrafts.com/a/thyearlyscheduleupdate',
      support_request: 'https://form.jotform.com/232355742236152',
    },
    pitpay: {
      new_event: 'https://form.jotform.com/232397277974069',
      event_change: 'https://form.jotform.com/232427336611047',
      help_site: 'https://help.pitpay.com',
      new_season: 'https://formcrafts.com/a/mcuvjzd',
      support_request: 'https://form.jotform.com/232427394013047',
    },
    kartpass: {
      new_event: 'https://form.jotform.com/241976637156165',
      event_change: 'https://form.jotform.com/241976363802159',
      help_site: 'https://help.kartpass.com',
      new_season: 'https://formcrafts.com/a/kpyearlyscheduleupdate',
      support_request: 'https://form.jotform.com/241976470415158',
    },
  };

  return forms[platform][form];
};

const getName = () => {
  if (process.env.REACT_APP_PLATFORM === 'pitpay') {
    return 'Pit Pay';
  }

  if (process.env.REACT_APP_PLATFORM === 'kartpass') {
    return 'Kart Pass';
  }

  if (process.env.REACT_APP_PLATFORM === 'tickethoss') {
    return 'Ticket Hoss';
  }

  return 'Pit Pay';
};

const data = [
  {
    id: 1,
    title: 'Support',
    data: [
      {
        id: 1,
        icon: null,
        name: 'Submit New Event',
        link: getFormLink('new_event'),
      },
      {
        id: 2,
        icon: null,
        name: 'Submit Event Change',
        link: getFormLink('event_change'),
      },
      {
        id: 4,
        icon: null,
        name: `${getName()} Help Site`,
        link: getFormLink('help_site'),
      },
      {
        id: 5,
        icon: null,
        name: 'Submit Support Request',
        link: getFormLink('support_request'),
      },
      {
        id: 6,
        icon: 'mail',
        name:
          process.env.REACT_APP_PLATFORM !== 'tickethoss'
            ? 'events@pitpay.com'
            : 'support@TicketHoss.com',
        link:
          process.env.REACT_APP_PLATFORM !== 'tickethoss'
            ? 'mailto:events@pitpay.com'
            : 'mailto:support@TicketHoss.com',
      },
      { id: 7, icon: 'phone', name: '(855) 748-1290', link: 'tel:8557481290' },
      { id: 8, icon: 'phone', name: '(855) 748-7292', link: 'tel:8557487292' },
    ],
  },
  {
    id: 2,
    title: 'Headquarters',
    data: [
      { id: 1, icon: 'location', name: 'Pit Pay Inc. Charlotte, NC' },
      {
        id: 2,
        icon: 'mail',
        name: 'info@PitPay.com',
        link: 'mailto:info@PitPay.com',
      },
      { id: 3, icon: 'phone', name: '(855) 748-7291', link: 'tel:8557487291' },
    ],
  },
];

// Styled components
const SectionBlock = styled.div`
  padding-bottom: 28px;
  margin-bottom: 28px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionName = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0 0 12px 0;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 0;
`;

const ListItemText = styled.div`
  display: flex;
  flex: 1;
  margin-left: 22px;
`;

const ContactLink = styled.a`
  text-decoration: none;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  font-size: 16px;
  line-height: 24px;
  transition: color 0.15s ease;

  &:hover {
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }
`;

const FormLink = styled.a`
  text-decoration: underline;
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ContactText = styled.span`
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  font-size: 16px;
  line-height: 24px;
`;

const Note = styled.span`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin-left: 10px;
  font-size: 14px;
  font-weight: 600;
`;

const SocialHeading = styled.h4`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0 0 16px 0;
`;

const SocialContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialItem = styled.a`
  padding: 10px;
  border-radius: 8px;
  background-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover {
    opacity: 0.85;
  }
`;

const Contact = () => {
  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <HeaderContainer>
            <PageHeaderIcon>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </PageHeaderIcon>
            <HeaderTitle>Contact</HeaderTitle>
          </HeaderContainer>
        </Card>

        {/* Content Card */}
        <Card noPadding>
          <ContentWrapper>
            {data.map((item, index) => (
              <SectionBlock
                key={item.id}
                style={index + 1 === data.length ? { marginBottom: 0 } : null}
              >
                <SectionName>{item.title}</SectionName>

                {item.data.map((subItem) => (
                  <ListItem key={subItem.id}>
                    <div>
                      <Icon icon={subItem.icon} size={22} color="#94A3B8" />
                    </div>
                    {subItem.icon ? (
                      <ListItemText>
                        {subItem.link ? (
                          <ContactLink href={subItem.link}>
                            {subItem.name}
                            {subItem.icon === 'phone' ? (
                              <Note>Text or Call</Note>
                            ) : null}
                          </ContactLink>
                        ) : (
                          <ContactText>
                            {subItem.name}
                            {subItem.icon === 'phone' ? (
                              <Note>Text or Call</Note>
                            ) : null}
                          </ContactText>
                        )}
                      </ListItemText>
                    ) : (
                      <FormLink href={subItem.link} target="_blank">
                        {subItem.name}
                      </FormLink>
                    )}
                  </ListItem>
                ))}
              </SectionBlock>
            ))}

            {/* Social Media */}
            <SectionBlock style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <SocialHeading>Follow Us</SocialHeading>
              <SocialContainer>
                <SocialItem href={getFacebookLink()} target="_blank">
                  <Icon icon="facebook" size={30} color="#fff" />
                </SocialItem>
                <SocialItem href={getInstagramLink()} target="_blank">
                  <Icon icon="instagram" size={30} color="#fff" />
                </SocialItem>
                <SocialItem href={getTwitterLink()} target="_blank">
                  <Icon icon="twitter" size={30} color="#fff" />
                </SocialItem>
                <SocialItem
                  href="https://www.linkedin.com/company/pitpay"
                  target="_blank"
                >
                  <Icon icon="linkedin" size={30} color="#fff" />
                </SocialItem>
              </SocialContainer>
            </SectionBlock>
          </ContentWrapper>
        </Card>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default Contact;
